// Did the browser actually open the popup we asked for?
//
// A blocked popup is silent: `window.open` returns null, the SDK that called it carries on, and
// the page waits forever for a window that never existed. That is the whole shape of the bug this
// exists for, so instead of guessing from a timeout we instrument the call and read the answer.

export type PopupOutcome = "opened" | "blocked" | "unknown";

/**
 * Runs `launch` with `window.open` instrumented and reports what happened to the popup.
 *
 * `unknown` is a real answer and the common one when a vendor SDK opens its window later than its
 * own entry point: only a call that RETURNED a null handle is reported as `blocked`, because a
 * false "tu navegador bloqueó la ventana" sends the operator chasing something that never happened.
 * The original `window.open` is always restored, including when `launch` throws.
 */
export function runWithPopupWatch(launch: () => void): PopupOutcome {
  const original = window.open;
  let outcome: PopupOutcome = "unknown";

  // The cast is only to re-attach the original signature after the wrapper's rest args; the
  // wrapper forwards every argument untouched and returns exactly what the real one returned.
  window.open = function patchedOpen(...args: Parameters<typeof window.open>) {
    const opened = original.apply(window, args);
    outcome = opened ? "opened" : "blocked";
    return opened;
  } as typeof window.open;

  try {
    launch();
  } finally {
    window.open = original;
  }

  return outcome;
}
